import api from 'services/api';

/**
 * The TrainerProps type defines the properties of a trainer, including their UUID, mobile user UUID,
 * space, tag, start time, and end time.
 * @property {string} uuid - This property is an optional string that represents the unique identifier
 * of a trainer.
 * @property {string} user - The `user` property is a required string that
 * represents the unique identifier of the mobile user associated with the trainer.
 * @property {string} space - The `space` property is a string that represents the physical location or
 * space where the training session will take place. It could be a gym, a park, a studio, or any other
 * location where the trainer and trainee agree to meet.
 * @property {string} tag - The "tag" property is a string that represents a label or identifier for
 * the trainer. It could be used to group or categorize trainers in some way.
 * @property {any} startTime - The `startTime` property is a optional property of type `any` in the
 * `TrainerProps` interface. It is used to specify the start time of a training session.
 * @property {any} endTime - `endTime` is a property of type `any` in the `TrainerProps` interface. It
 * represents the end time of a training session. It is optional and may not be present in all
 * instances of `TrainerProps`.
 */
export type TrainerProps = {
  uuid?: string;
  mobileUserUuid: string;
  spaceUuid: string;
  tag: string;
  start?: any;
  end?: any;
};

/**
 * This function creates a new trainer by sending a POST request to an API endpoint and returns the
 * response data.
 * @param {TrainerProps} newTrainer - The `newTrainer` parameter is an object of type `TrainerProps`
 * which contains the properties of a new trainer that will be created. This object is passed as the
 * second argument to the `api.post` method.
 * @returns The `createTrainer` function is returning the `data` property of the response object
 * returned by the `api.post` method. This is of type `TrainerProps`, which is the same type as the
 * `newTrainer` parameter passed to the function.
 */
const createTrainer = async (newTrainer: TrainerProps) => {
  const response = await api.post<TrainerProps>('/apiv1/train/', newTrainer);
  return response?.data;
};

/* `const TrainingService` is an object that contains a single property `createTrainer`, which is a
reference to the `createTrainer` function defined above. This object is then exported as the default
export of the module, which means that other modules can import it and access the `createTrainer`
function by calling `TrainingService.createTrainer()`. This is a common pattern in JavaScript and
TypeScript for organizing related functions or methods into a single object or class. */
const TrainingService = {
  createTrainer
};

export default TrainingService;
